import React, {useCallback, useEffect, useReducer} from "react";
import {
  CreateNewProjectAction,
  ICreateNewProjectState,
  initialCreateNewProjectState,
  newProjectReducer
} from "../../reducers/createNewProjectReducer.ts";
import {useSteps} from "@chakra-ui/react";
import {CREATE_NEW_PROJECT_STEPS} from "../../models/constants/constants.ts";
import CreateProjectSteps from "./CreateProjectSteps.tsx";
import StepUploadFiles from "./SetupUploadFiles.tsx";
import CreateProjectModalFooter from "./CreateProjectModalFooter.tsx";
import SubtitlesStep from "./SubtitlesStep.tsx";
import {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import {getFileBaseName} from "../../utils/utils.ts";
import {useProjectsStore} from "../../store/useProjectsStore.ts";
import {IProject} from "../../models/interfaces/project.interface.ts";
import MetadataStep from "./MetadataStep.tsx";
import {GranularityEnum} from "../../models/enums/granularity.enum.ts";
import {VisualTagsEnum} from "../../models/enums/visualTags.enum.ts";
import AdvancedStep from "./AdvencedStep.tsx";
import useUserStore, {IUserState} from "../../store/useUserStore.ts";
import {NewProjectConfiguration, NewProjectConfigurations} from "../../models/interfaces/newProjectConfigurations.ts";

interface CreateNewProjectModalProps {
  remainingMinutes?: number;
  createNewProject: (state: ICreateNewProjectState) => Promise<void>;
}

const CreateNewProjectModal: React.FC<CreateNewProjectModalProps> = (
  {
    remainingMinutes,
    createNewProject,
  }: CreateNewProjectModalProps) => {
  const newProjectCreationConfiguration: NewProjectConfigurations = useUserStore((state: IUserState) => state.preferences?.newProjectCreation || {});
  const [state, dispatch] = useReducer<ICreateNewProjectState, [action: CreateNewProjectAction]>(newProjectReducer, initialCreateNewProjectState);
  const {projects} = useProjectsStore()
  const {activeStep, goToNext, goToPrevious} = useSteps({
    index: 0,
    count: CREATE_NEW_PROJECT_STEPS.length,
  })

  const handleErrors: (error: string, action?: "add" | "remove") => void = useCallback(
    (error: string, action: "add" | "remove" = "add") => {
      if (state.errors.includes(error) && action == "add") return;
      if (action === "remove") {
        dispatch({type: "SET_ERRORS", payload: state.errors.filter((e) => e !== error)});
        return;
      }
      dispatch({type: "SET_ERRORS", payload: [...state.errors, error]});
    }, [dispatch, state.errors]);

  const canContinueToNextStepHandler: () => boolean = (): boolean => {
    if (activeStep === 0) {

      const hasUploadedFiles: boolean = state.attachedFiles.length > 0;
      const hasDuration: boolean = Boolean(state.projectTotalDuration > 0);

      const allProjectNames: string[] = projects.map((p: IProject) => p.name);
      const hasProjectNameDuplicateError: boolean = state.attachedFiles.some((file: File) =>
        allProjectNames.includes(getFileBaseName(file.name))
      );
      if (hasProjectNameDuplicateError) {
        handleErrors("Project name already exists");
      } else if (!hasProjectNameDuplicateError && state.errors.includes("Project name already exists")) {
        handleErrors("Project name already exists", "remove");
      }
      const hasMinutesError: boolean = state.projectTotalDuration > (remainingMinutes || 0);
      const hasEmptyNameError: boolean = state.attachedFiles.some((file: File) =>
        getFileBaseName(file.name) == ""
      );
      if (hasEmptyNameError) {
        handleErrors("Project name cannot be empty");
      } else if (!hasEmptyNameError && state.errors.includes("Project name cannot be empty")) {
        handleErrors("Project name cannot be empty", "remove");
      }

      return hasUploadedFiles && hasDuration && !hasProjectNameDuplicateError && !hasMinutesError && !hasEmptyNameError;
    }
    return true;
  };

  const canProceed: boolean = canContinueToNextStepHandler();
  const isCreateDisabled: boolean = (
    state.errors.length > 0 ||
    state.attachedFiles.length === 0 ||
    Object.keys(state.subtitles.sourceLanguage).length === 0 ||
    (!state.subtitles.isTranscribeOnly && Object.keys(state.subtitles.targetLanguages).length === 0)
  );

  const setGranularity: (granularity: GranularityEnum) => void = useCallback(
    (granularity: GranularityEnum) => dispatch({type: "SET_GRANULARITY", payload: granularity}),
    [dispatch]
  );

  const setShotChangeDetectionSensitivity: (value: number) => void = useCallback(
    (value: number) => dispatch({type: "SET_SHOT_CHANGE_DETECTION_SENSITIVITY", payload: value}),
    [dispatch]
  );

  const setVisualTags: (visualTags: VisualTagsEnum[]) => void = useCallback(
    (visualTags: VisualTagsEnum[]) => dispatch({type: "SET_VISUAL_TAGS", payload: visualTags}),
    [dispatch]
  );

  const setAudioDescriptionLanguages: (languages: string[]) => void = useCallback(
    (languages: string[]) => dispatch({type: "SET_AUDIO_DESCRIPTION", payload: languages}),
    [dispatch]
  );

  useEffect(() => {
    if (state.customConfigurationId) {
      const configuration: NewProjectConfiguration = newProjectCreationConfiguration[state.customConfigurationId];
      dispatch({type: "FILL_ALL_STEPS_WITH_CONFIGURATION", payload: configuration});
    }
  }, [state.customConfigurationId, newProjectCreationConfiguration]);

  return (
    <div className="flex flex-col justify-between p-12 min-h-[90vh]">
      <div className="flex-1 flex flex-col min-h-0">
        <CreateProjectSteps
          steps={CREATE_NEW_PROJECT_STEPS}
          activeStep={activeStep}
        />
        {activeStep === 0 && (
          <StepUploadFiles
            uploadedFiles={state.attachedFiles}
            customConfigurationId={state.customConfigurationId}
            setUploadedFiles={(files: File[]) => dispatch({type: "SET_FILES", payload: files})}
            fileNameToSeriesNameRelation={state.fileNameToSeriesNameRelation}
            setFileNameToSeriesNameRelation={(relation: Record<string, string>) => dispatch({
              type: "SET_FILE_SERIES_RELATION",
              payload: relation
            })}
            setCustomConfiguration={(configId: string | undefined) => dispatch({
              type: "SET_CUSTOM_CONFIGURATION_ID",
              payload: configId
            })}
            remainingMinutes={remainingMinutes}
            projectTotalDuration={state.projectTotalDuration}
            setVideoDurationInMinutes={(duration: number) => dispatch({
              type: "SET_PROJECT_TOTAL_DURATION",
              payload: duration
            })}
            handleErrors={handleErrors}
          />
        )}
        {activeStep == 1 && (
          <SubtitlesStep
            remainingMinutes={remainingMinutes}
            totalDurationInMinutes={state.projectTotalDuration}
            goToNext={goToNext}
            goToPrevious={goToPrevious}
            subtitlesState={state.subtitles}
            setSourceLanguage={(payload: Record<string, ILanguageConfiguration>) => dispatch({
              type: "SET_SOURCE_LANGUAGE",
              payload: payload
            })}
            toggleIsTranscribeOnly={() => dispatch({type: "TOGGLE_TRANSCRIBE_ONLY"})}
            setTargetLanguage={(payload: Record<string, ILanguageConfiguration>) => dispatch({
              type: "SET_TARGET_LANGUAGES",
              payload
            })}
            toggleFeature={(feature: string) => dispatch({type: "TOGGLE_FEATURES", payload: feature})}
          />
        )}
        {activeStep === 2 && (
          <>
            <MetadataStep
              metadataState={state.metadata}
              toggleDialogueList={(value: boolean) => dispatch({
                type: "SET_IS_DIALOGUE_LIST_SELECTED",
                payload: value
              })}
              toggleExtendedMetadata={(value: boolean) => dispatch({
                type: "SET_IS_EXTENDED_METADATA_SELECTED",
                payload: value
              })}
              setGranularity={setGranularity}
              setShotChangeDetectionSensitivity={setShotChangeDetectionSensitivity}
              setVisualTags={setVisualTags}
            />
          </>
        )}
        {activeStep === 3 && (
          <AdvancedStep
            advancedState={state.advanced}
            toggleAudioDescription={(_) => dispatch({type: "TOGGLE_AUDIO_DESCRIPTION"})}
            setAudioDescriptionLanguages={setAudioDescriptionLanguages}
            configurationName={state.customConfigurationName}
            setConfigurationName={(name: string) => dispatch({type: "SET_CUSTOM_CONFIGURATION_NAME", payload: name})}
          />

        )}
        {
          state.errors.length > 0 && (
            <div className="mt-4 max-h-20 overflow-y-auto">
              <ul className="list-disc pl-5 text-red-500">
                {state.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )
        }
      </div>
      {/* Footer */}
      {activeStep !== 1 && <div className="flex justify-end mt-6 gap-4">
          <CreateProjectModalFooter
              activeStep={activeStep}
              goToNext={goToNext}
              goToPrevious={goToPrevious}
              finalStep={CREATE_NEW_PROJECT_STEPS.length - 1}
              finalStepTitle="Create"
              canContinueToNextStep={canProceed}
              isFinalStepDisabled={isCreateDisabled}
              customConfigurationId={state.customConfigurationId}
              onFinalStepClicked={() => createNewProject(state)}
          />
      </div>
      }
    </div>
  );
};

export default CreateNewProjectModal;